import api from './api';

/** One mill and ingredient: short tons per month, for the months that are set. Months look like "2026-11". */
export interface RequirementRow {
  mill_id: string;
  mill_name: string;
  ingredient_id: string;
  ingredient_name: string;
  /** False when the mill or ingredient is switched off: set cells can change, empty ones take nothing new. */
  active: boolean;
  short_tons: Record<string, string>;
}

export interface RequirementGrid {
  months: string[];
  rows: RequirementRow[];
  totals: { ingredient_id: string; ingredient_name: string; short_tons: Record<string, string> }[];
}

/** One cell to save; null clears it. */
export interface RequirementCell {
  mill_id: string;
  ingredient_id: string;
  month: string;
  short_tons: string | null;
  /** What the cell held when it was checked; sent, the save is refused if it has changed since. */
  old_short_tons?: string | null;
}

/** One change to one cell: what it was, what it became, who and when. */
export interface RequirementChange {
  id: string;
  mill_id: string;
  mill_name: string;
  ingredient_id: string;
  ingredient_name: string;
  month: string;
  old_short_tons: string | null;
  new_short_tons: string | null;
  changed_by: string;
  source: string;
  changed_at: string;
}

/** One cell a checked file would change; old is null when the cell is empty now. */
export interface ImportChange {
  mill_id: string;
  mill_name: string;
  ingredient_id: string;
  ingredient_name: string;
  month: string;
  old_short_tons: string | null;
  new_short_tons: string;
}

/** What saving a file would do. Nothing is saved until the changes are sent back. */
export interface ImportPreview {
  changes: ImportChange[];
  unchanged: number;
  problems: { row: number; message: string }[];
}

export const requirementsApi = {
  grid: (slug: string, start: string, months = 12) =>
    api.get<RequirementGrid>(`/buyers/${slug}/requirements`, { params: { start, months } }),
  save: (slug: string, cells: RequirementCell[], source: 'screen' | 'import' = 'screen') =>
    api.put<{ changed: number }>(`/buyers/${slug}/requirements`, { cells, source }),
  template: (slug: string, start: string, months = 12) =>
    api.get<Blob>(`/buyers/${slug}/requirements/template`, { params: { start, months }, responseType: 'blob' }),
  previewImport: (slug: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    // The client defaults to JSON, which would turn the form into JSON; this lets the browser add the boundary.
    return api.post<ImportPreview>(`/buyers/${slug}/requirements/import/preview`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  history: (slug: string, params: { mill_id?: string; limit?: number } = {}) =>
    api.get<RequirementChange[]>(`/buyers/${slug}/requirements/history`, { params }),
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-11" as "Nov 2026". */
export function monthLabel(month: string) {
  const [year, m] = month.split('-');
  return `${MONTH_NAMES[Number(m) - 1]} ${year}`;
}

/** "2026-11" moved by a number of months, e.g. ("2026-11", 3) is "2027-02". */
export function shiftMonth(month: string, count: number) {
  const [year, m] = month.split('-').map(Number);
  const index = year * 12 + (m - 1) + count;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** This month, as "2026-10". */
export function currentMonth(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Short tons as typed: blank, or a number of no more than two decimals. */
export function validTons(value: string) {
  return value.trim() === '' || /^\d{1,10}(\.\d{1,2})?$/.test(value.trim());
}

/** "1200.50" as "1,200.5"; whole numbers keep no decimals. */
export function formatTons(value: string | number) {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
}
